import { Injectable, inject } from '@angular/core';
import { AuthHelpers, AuthService } from '@eview/core/auth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap
} from 'rxjs/operators';
import { isEqual } from 'lodash';
import { selectConfig } from '../selectors/config.selector';
import { selectPermissions } from '../selectors/permissions.selector';
import { selectRoles } from '../selectors/roles.selector';
import { selectUser } from '../selectors/user.selector';
import { UserLoggedIn } from '../actions/app.actions';
import {
  ClearPermissions,
  ClearRoles,
  ClearUserPermissions,
  EAuthActions,
  ForgotPassword,
  ForgotPasswordFailure,
  ForgotPasswordFailureReason,
  ForgotPasswordSuccess,
  GetCurrentUser,
  GetCurrentUserFailure,
  GetCurrentUserSuccess,
  Login,
  LoginFailure,
  LoginSuccess,
  Logout,
  LogoutSuccess,
  SetUserPermissions
} from '../actions/auth.actions';
import { ClearMapActual } from '../actions/map.actions';
import { StopNotifications } from '../actions/notifications.actions';
import { AppState } from '../states/app.state';
import { logAuthError, logAuthWarn } from '../../auth/auth-debug.helpers';
@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store<AppState>);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType<Login>(EAuthActions.Login),
      map(action => action.payload),
      switchMap(payload =>
        this.authService.sendRequest(payload).pipe(
          switchMap(loginResponse => {
            if (!loginResponse) {
              logAuthWarn('login$: no token from oauth/token');
              return of(new LoginFailure());
            }
            return this.authService.me().pipe(
              switchMap(user => {
                if (!user) {
                  logAuthWarn('login$: /users/me returned empty');
                  return of(new LoginFailure());
                }
                return [
                  new LoginSuccess(user),
                  new GetCurrentUserSuccess(user),
                  new UserLoggedIn()
                ];
              })
            );
          }),
          catchError((e) => {
            logAuthError('login$: sendRequest or me failed', e);
            return of(new LoginFailure());
          })
        )
      ),
      catchError((e) => {
        logAuthError('login$: outer catch', e);
        return of(new LoginFailure());
      })
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ForgotPassword>(EAuthActions.ForgotPassword),
      map(action => action.payload),
      switchMap(payload =>
        this.authService.forgotPassword(payload).pipe(
          switchMap(loginResponse => {
            if (!loginResponse) {
              return of(new ForgotPasswordFailure('network'));
            }
            // Treat success and legacy mail_failed the same so callers cannot
            // enumerate registered emails from the API response shape.
            if (loginResponse.status || loginResponse.message === 'mail_failed') {
              return of(new ForgotPasswordSuccess(loginResponse));
            }
            return of(new ForgotPasswordFailure('network'));
          }),
          catchError((err: unknown) =>
            of(new ForgotPasswordFailure(mapForgotPasswordError(err)))
          )
        )
      ),
      catchError((err: unknown) =>
        of(new ForgotPasswordFailure(mapForgotPasswordError(err)))
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType<Logout>(EAuthActions.Logout),
      switchMap(() => {
        this.authService.clearToken('logout$ effect');
        return of(
          new LogoutSuccess(),
          new ClearUserPermissions(),
          new ClearRoles(),
          new ClearPermissions(),
          new ClearMapActual(),
          new StopNotifications()
        );
      })
    )
  );

  getCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCurrentUser>(EAuthActions.GetCurrentUser),
      switchMap(() =>
        this.authService.me().pipe(
          switchMap(user => {
            if (!user) {
              logAuthWarn('getCurrentUser$: /users/me returned empty');
              return of(new GetCurrentUserFailure());
            }
            return of(new GetCurrentUserSuccess(user));
          }),
          catchError((e) => {
            logAuthError('getCurrentUser$: /users/me failed', e);
            return of(new GetCurrentUserFailure());
          })
        )
      ),
      catchError((e) => {
        logAuthError('getCurrentUser$: outer catch', e);
        return of(new GetCurrentUserFailure());
      })
    )
  );

  /**
   * Unblock menus/data as soon as /users/me returns (permissions + form_grants).
   * Does not wait for full /roles + /permissions catalogs.
   */
  setUserPermissionsFromMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCurrentUserSuccess>(EAuthActions.GetCurrentUserSuccess),
      map(action =>
        new SetUserPermissions(
          AuthHelpers.User.GetUserPermissionsFromMe(action.payload)
        )
      )
    )
  );

  /**
   * Recompute when catalogs arrive (admin screens / enrichment).
   * Optional paths: permissions/roles may still be null after refresh until deferred load.
   */
  getUserPermissions$ = createEffect(() =>
    combineLatest([
      this.store.select(selectUser),
      this.store.select(selectConfig),
      this.store.select(selectPermissions),
      this.store.select(selectRoles)
    ]).pipe(
      filter(([user, config]) => !!user && !!config),
      // Prefer enriching only when catalogs available; me path already set store
      filter(([, , permissions, roles]) => !!permissions && !!roles),
      map(([user, config, permissions, roles]) =>
        AuthHelpers.User.GetUserPermissions(
          user,
          config,
          permissions,
          roles
        )
      ),
      distinctUntilChanged((a, b) => isEqual(a, b)),
      map(payload => new SetUserPermissions(payload))
    )
  );
}

function mapForgotPasswordError(err: unknown): ForgotPasswordFailureReason {
  if (err instanceof HttpErrorResponse) {
    if (err.status === 422) {
      return 'validation';
    }
    if (err.status === 401 && err.error?.error === 'invalid_credentials') {
      return 'recaptcha';
    }
  }
  return 'network';
}
